import type { QueryClient } from '@tanstack/react-query'
import type { OptionItem } from './property.type'
import { listQueryPrefix, optionsByFieldQueryPrefix } from './property.keys'

type Patch = (item: OptionItem) => OptionItem
export type OptionCacheOperation = {
  listId: string | undefined
  patch: Patch
  previous: Map<string, OptionItem>
}
type ListState = {
  operations: Set<OptionCacheOperation>
  waiters: Array<() => void>
  timer?: ReturnType<typeof setTimeout>
}
const clients = new WeakMap<QueryClient, Map<string | undefined, ListState>>()
const writes = new WeakMap<QueryClient, Map<string, Promise<unknown>>>()
const stateFor = (client: QueryClient, listId: string | undefined) => {
  let lists = clients.get(client)
  if (!lists) clients.set(client, (lists = new Map()))
  let state = lists.get(listId)
  if (!state)
    lists.set(listId, (state = { operations: new Set(), waiters: [] }))
  return state
}
const walk = (items: OptionItem[], patch: Patch): OptionItem[] =>
  items.map((item) => {
    const next = patch(item)
    return next.options
      ? {
          ...next,
          options: { ...next.options, items: walk(next.options.items, patch) },
        }
      : next
  })
export const optionCacheQueries = (
  client: QueryClient,
  listId: string | undefined,
) => [
  ...client.getQueriesData<OptionItem[]>({ queryKey: listQueryPrefix(listId) }),
  ...client.getQueriesData<OptionItem[]>({
    queryKey: optionsByFieldQueryPrefix(listId),
  }),
]
export const patchOptionCache = (
  client: QueryClient,
  listId: string | undefined,
  patch: Patch,
) => {
  for (const [key, items] of optionCacheQueries(client, listId)) {
    if (Array.isArray(items)) client.setQueryData(key, walk(items, patch))
  }
}
export const protectPendingOptionItems = (
  client: QueryClient,
  listId: string | undefined,
  items: OptionItem[],
) => {
  for (const operation of stateFor(client, listId).operations)
    items = walk(items, operation.patch)
  return items
}
export const beginOptionCacheOperation = async (
  client: QueryClient,
  listId: string | undefined,
  patch: Patch,
) => {
  const state = stateFor(client, listId)
  if (state.timer) clearTimeout(state.timer)
  const operation: OptionCacheOperation = { listId, patch, previous: new Map() }
  state.operations.add(operation)
  await Promise.all([
    client.cancelQueries({ queryKey: listQueryPrefix(listId) }),
    client.cancelQueries({ queryKey: optionsByFieldQueryPrefix(listId) }),
  ])
  for (const [key, items] of optionCacheQueries(client, listId)) {
    if (!Array.isArray(items)) continue
    client.setQueryData(
      key,
      walk(items, (item) => {
        const next = patch(item)
        if (next !== item)
          operation.previous.set(JSON.stringify([key, item.id]), item)
        return next
      }),
    )
  }
  return operation
}
// Roll back only fields still carrying this operation's value. Later edits and
// successful writes to other fields or rows remain intact.
export const settleOptionCacheOperation = async (
  client: QueryClient,
  operation: OptionCacheOperation,
  failed: boolean,
  canonical?: Patch,
) => {
  // Reads started after onMutate may still be in flight. Cancel them before
  // releasing this patch so an older response cannot overwrite its settlement.
  // Await cancellation's error transition before setQueryData restores success;
  // revert:false keeps concurrent edits while these reads are being cancelled.
  await Promise.all([
    client.cancelQueries(
      { queryKey: listQueryPrefix(operation.listId) },
      { revert: false },
    ),
    client.cancelQueries(
      { queryKey: optionsByFieldQueryPrefix(operation.listId) },
      { revert: false },
    ),
  ])
  const pendingOperations = [...stateFor(client, operation.listId).operations]
  const newerOperations = pendingOperations.slice(
    pendingOperations.indexOf(operation) + 1,
  )
  for (const [key, items] of optionCacheQueries(client, operation.listId)) {
    if (!Array.isArray(items)) continue
    client.setQueryData(
      key,
      walk(items, (current) => {
        const previous = operation.previous.get(
          JSON.stringify([key, current.id]),
        )
        if (!previous) return current
        const expected = operation.patch(previous)
        const confirmed = canonical?.(expected) ?? expected
        const result = { ...current } as Record<string, unknown>
        const hasNewerEdit = (field: keyof OptionItem, lang?: string) =>
          newerOperations.some((pending) => {
            const before = pending.previous.get(
              JSON.stringify([key, current.id]),
            )
            if (!before) return false
            const after = pending.patch(before)
            if (field === 'translations' && lang)
              return (
                JSON.stringify(
                  before.translations?.items.find(
                    (entry) => entry.lang === lang,
                  ),
                ) !==
                JSON.stringify(
                  after.translations?.items.find(
                    (entry) => entry.lang === lang,
                  ),
                )
              )
            return (
              JSON.stringify(before[field]) !== JSON.stringify(after[field])
            )
          })

        for (const field of new Set([
          ...Object.keys(expected),
          ...Object.keys(confirmed),
        ])) {
          if (field === 'options') continue
          if (
            field === 'updatedAt' &&
            canonical &&
            !failed &&
            confirmed.updatedAt !== expected.updatedAt
          ) {
            result.updatedAt = confirmed.updatedAt
            continue
          }
          if (field === 'translations') {
            const before = previous.translations?.items ?? []
            const after = expected.translations?.items ?? []
            const committed = confirmed.translations?.items ?? []
            let translations = current.translations?.items ?? []
            for (const translation of after) {
              if (hasNewerEdit('translations', translation.lang)) continue
              const old = before.find(
                (entry) => entry.lang === translation.lang,
              )
              if (JSON.stringify(old) === JSON.stringify(translation)) continue
              const live = translations.find(
                (entry) => entry.lang === translation.lang,
              )
              if (JSON.stringify(live) !== JSON.stringify(translation)) continue
              const replacement = failed
                ? old
                : committed.find((entry) => entry.lang === translation.lang)
              translations = [
                ...translations.filter(
                  (entry) => entry.lang !== translation.lang,
                ),
                ...(replacement ? [replacement] : []),
              ]
            }
            result.translations = {
              ...current.translations,
              items: translations,
            }
            continue
          }
          if (hasNewerEdit(field as keyof OptionItem)) continue
          if (
            JSON.stringify(expected[field as keyof OptionItem]) ===
              JSON.stringify(previous[field as keyof OptionItem]) &&
            !canonical
          )
            continue
          if (
            JSON.stringify(current[field as keyof OptionItem]) !==
            JSON.stringify(expected[field as keyof OptionItem])
          )
            continue
          result[field] = failed
            ? previous[field as keyof OptionItem]
            : confirmed[field as keyof OptionItem]
        }
        return result as OptionItem
      }),
    )
  }
  if (failed || canonical) {
    for (const pending of stateFor(client, operation.listId).operations) {
      if (pending === operation) continue
      for (const [key, beforePending] of pending.previous) {
        const original = operation.previous.get(key)
        if (!original) continue
        const expected = operation.patch(original)
        const baseline = failed ? original : (canonical?.(expected) ?? expected)
        const rebased = { ...beforePending } as Record<string, unknown>
        for (const field of Object.keys(expected)) {
          if (field === 'options') continue
          if (field === 'translations') {
            let translations = beforePending.translations?.items ?? []
            for (const translation of expected.translations?.items ?? []) {
              const old = baseline.translations?.items.find(
                (entry) => entry.lang === translation.lang,
              )
              if (JSON.stringify(old) === JSON.stringify(translation)) continue
              const live = translations.find(
                (entry) => entry.lang === translation.lang,
              )
              if (JSON.stringify(live) !== JSON.stringify(translation)) continue
              translations = [
                ...translations.filter(
                  (entry) => entry.lang !== translation.lang,
                ),
                ...(old ? [old] : []),
              ]
            }
            rebased.translations = {
              ...beforePending.translations,
              items: translations,
            }
            continue
          }
          if (
            JSON.stringify(beforePending[field as keyof OptionItem]) ===
            JSON.stringify(expected[field as keyof OptionItem])
          )
            rebased[field] = baseline[field as keyof OptionItem]
        }
        pending.previous.set(key, rebased as OptionItem)
      }
    }
  }
  stateFor(client, operation.listId).operations.delete(operation)
}
export const reconcileOptionCache = (
  client: QueryClient,
  listId: string | undefined,
) => {
  const state = stateFor(client, listId)
  const promise = new Promise<void>((resolve) => state.waiters.push(resolve))
  if (state.operations.size === 0) {
    if (state.timer) clearTimeout(state.timer)
    state.timer = setTimeout(() => {
      state.timer = undefined
      const waiters = state.waiters.splice(0)
      void Promise.all([
        client.invalidateQueries({
          queryKey: listQueryPrefix(listId),
          refetchType: 'active',
        }),
        client.invalidateQueries({
          queryKey: optionsByFieldQueryPrefix(listId),
          refetchType: 'active',
        }),
      ])
        .catch(() => undefined)
        .finally(() => waiters.forEach((resolve) => resolve()))
    }, 50)
  }
  return promise
}
// Same entity writes retain server order; unrelated rows remain parallel.
export const orderedOptionWrite = async <T>(
  client: QueryClient,
  key: string,
  write: () => Promise<T>,
): Promise<T> => {
  let queues = writes.get(client)
  if (!queues) writes.set(client, (queues = new Map()))
  const previous = queues.get(key)
  const next = (
    previous ? previous.catch(() => undefined) : Promise.resolve()
  ).then(write)
  queues.set(key, next)
  try {
    return await next
  } finally {
    if (queues.get(key) === next) queues.delete(key)
  }
}
export const findCachedOption = (
  client: QueryClient,
  listId: string | undefined,
  id: string,
) => {
  let found: OptionItem | undefined
  for (const [, items] of optionCacheQueries(client, listId))
    if (Array.isArray(items))
      walk(items, (item) => {
        if (item.id === id) found = item
        return item
      })
  return found
}
